/**
 * *"Who do you want to be?"* ([D-158](../../../docs/DECISIONS.md#d-158), mockup 33).
 *
 * Twenty animals and eight colours, all free and changeable any time
 * ([D-036](../../../docs/DECISIONS.md#d-036)): who you are is identity, not
 * something to earn. The colours tint the card *behind* the animal, never the
 * fur — recolouring a panda breaks the panda — and the screen says so.
 *
 * Nothing is saved until *"That's me!"*: tapping around the zoo is trying
 * things on, and the big preview shows each one wearing what the child already
 * has on. There is no name field. A pet name for your animal would be typed
 * free text about a child that the privacy policy does not list, and the
 * animal is already theirs without one.
 */

import '@capytalk/tokens/blocks/avatar.css';
import {
  AVATAR_BACKGROUNDS,
  type AvatarBackground,
  type AvatarSpecies,
} from '@capytalk/core';
import { AVATARS } from '@capytalk/tokens';
import { useState } from 'react';
import Animal from './Animal.js';
import type { Client } from './api.js';
import Sneakers from './Sneakers.js';

type Look = NonNullable<Awaited<ReturnType<Client['shop']['chooseLook']['mutate']>>>;

/** What a screen reader says for each swatch. */
const COLOUR_NAMES: Readonly<Record<AvatarBackground, string>> = {
  '#E08A4B': 'orange',
  '#8C6A4F': 'brown',
  '#D9D2C5': 'sand',
  '#7FC3A8': 'mint',
  '#7FA9DB': 'blue',
  '#C79BD6': 'lilac',
  '#E8A0B0': 'pink',
  '#F2C94C': 'yellow',
};

const offered = (colour: string): colour is AvatarBackground =>
  (AVATAR_BACKGROUNDS as readonly string[]).includes(colour);

/**
 * The card a child starts on. Children added before the picker have a pale
 * blue that is not one of the eight, so they start on the nearest one rather
 * than on nothing selected.
 */
const startColour = (colour: string): AvatarBackground =>
  offered(colour) ? colour : '#7FA9DB';

export default function AnimalPicker({
  client,
  look,
  onDone,
}: {
  readonly client: Client;
  readonly look: {
    readonly species: string;
    readonly background: string;
    readonly worn?: Parameters<typeof Animal>[0]['worn'];
  };
  /** With what the server saved, or null for *back* and for a failed save. */
  readonly onDone: (saved: Look | null) => void;
}) {
  const [species, setSpecies] = useState<string>(look.species);
  const [background, setBackground] = useState<AvatarBackground>(
    startColour(look.background),
  );
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState(false);

  const name = AVATARS.find((one) => one.id === species)?.name ?? 'Pick one';
  const picked = AVATARS.some((one) => one.id === species);

  const save = () => {
    if (!picked) return;
    setBusy(true);
    setTrouble(false);
    Promise.resolve()
      .then(() =>
        client.shop.chooseLook.mutate({
          species: species as AvatarSpecies,
          background,
        }),
      )
      .then((saved) => {
        onDone(saved);
      })
      .catch(() => {
        setBusy(false);
        setTrouble(true);
      });
  };

  return (
    <div className="grow pad" style={{ paddingTop: 8 }}>
      <h2>Who do you want to be?</h2>
      <div style={{ marginTop: 10 }}>
        <Sneakers
          text="Pick an animal! You can change it any time."
          audioRef={null}
          size={46}
        />
      </div>

      <div
        className="stage"
        style={{
          marginTop: 14,
          padding: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          textAlign: 'left',
        }}
      >
        <Animal
          species={species}
          background={background}
          worn={look.worn}
          width={80}
          uid="pick-preview"
          label={`You, as the ${name.toLowerCase()}`}
        />
        <div style={{ fontWeight: 800, fontSize: 19 }} aria-live="polite">
          {name}
        </div>
      </div>

      <div className="kicker" style={{ margin: '12px 0 7px' }}>
        ALL FREE — PICK ANY
      </div>
      <div className="zoo" role="group" aria-label="Animals">
        {AVATARS.map((one) => (
          <button
            key={one.id}
            type="button"
            className={species === one.id ? 'an on' : 'an'}
            aria-pressed={species === one.id}
            aria-label={one.name}
            style={{ font: 'inherit', cursor: 'pointer', minHeight: 48 }}
            onClick={() => {
              setSpecies(one.id);
            }}
          >
            <Animal
              species={one.id}
              background="transparent"
              width={38}
              uid={`pick-${one.id}`}
            />
          </button>
        ))}
      </div>

      <div className="kicker" style={{ margin: '12px 0 2px' }}>
        BACKGROUND COLOUR
      </div>
      <div className="tiny" style={{ marginBottom: 8 }}>
        The colour behind your animal
      </div>
      <div className="swatches" role="group" aria-label="Background colours">
        {AVATAR_BACKGROUNDS.map((colour) => (
          <button
            key={colour}
            type="button"
            className={background === colour ? 'sw2 on' : 'sw2'}
            aria-pressed={background === colour}
            aria-label={COLOUR_NAMES[colour]}
            style={{ background: colour, cursor: 'pointer', padding: 0 }}
            onClick={() => {
              setBackground(colour);
            }}
          />
        ))}
      </div>

      {trouble && (
        <p className="sub" role="alert" style={{ marginTop: 12 }}>
          That did not save. Please try again.
        </p>
      )}

      <button
        type="button"
        className="btn"
        style={{ marginTop: 18 }}
        disabled={busy || !picked}
        onClick={save}
      >
        That’s me!
      </button>
      <button
        type="button"
        className="btn ghost"
        style={{ marginTop: 10, marginBottom: 20 }}
        disabled={busy}
        onClick={() => {
          onDone(null);
        }}
      >
        Back
      </button>
    </div>
  );
}
